import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, PaperPlaneRight, Trash, ImageSquare, Buildings, ArrowLeft, User, Wrench, FileText, ShoppingCart, X, AddressBook } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { ReservationsTab } from '../components/projects/ReservationsTab';
import { ProjectDocumentsViewer } from '../components/projects/ProjectDocumentsViewer';
import { MaterialsListTab } from '../components/projects/MaterialsListTab';
import { PhotosTabGrouped } from '../components/projects/PhotosTabGrouped';
import { PhotoPreviewModal } from '../components/projects/ProjectModals';
import { ProjectContactsTab } from '../components/projects/ProjectContactsTab';
import { fmtDateTimeFR } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const timeStr = `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    if (d.toDateString() === today.toDateString()) return `Aujourd'hui ${timeStr}`;
    return fmtDateTimeFR(iso);
  } catch { return ''; }
};

const ProjectCard = ({ project, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`my-project-card-${project.id}`}
    style={{
      textAlign: 'left', background: 'white', border: '1px solid #E2E2E6',
      borderRadius: '10px', padding: '18px 20px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: '14px',
      transition: 'box-shadow 0.15s, transform 0.15s, border-color 0.15s',
      width: '100%'
    }}
    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 47, 167, 0.12)'; e.currentTarget.style.borderColor = '#002FA7'; }}
    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#E2E2E6'; }}
  >
    <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'linear-gradient(135deg, #002FA7, #3D5AFE)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Buildings size={22} weight="bold" />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: '700', color: '#1E2030', fontSize: '1rem' }}>{project.name}</div>
      {project.client_name && <div style={{ fontSize: '0.82rem', color: '#5E5F66' }}>{project.client_name}</div>}
    </div>
    <Camera size={18} color="#9E9EA7" />
  </button>
);

const FeedEntry = ({ entry, currentUserId, isAdmin, onDelete }) => {
  const canDelete = isAdmin || entry.author_id === currentUserId;
  // Support both legacy single photo_url and new photo_urls[] (since iter66)
  const photos = (entry.photo_urls && entry.photo_urls.length > 0)
    ? entry.photo_urls
    : (entry.photo_url ? [entry.photo_url] : []);
  return (
    <div data-testid={`feed-entry-${entry.id}`} style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: entry.author_role === 'admin' ? '#002FA7' : '#2E7D32', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: '700' }}>
            {(entry.author_name || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.88rem', color: '#1E2030' }}>{entry.author_name}</div>
            <div style={{ fontSize: '0.72rem', color: '#9E9EA7' }}>{fmtTime(entry.created_at)}</div>
          </div>
        </div>
        {canDelete && (
          <button
            onClick={() => onDelete(entry.id)}
            data-testid={`feed-delete-${entry.id}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', padding: '4px' }}
            title="Supprimer"
          >
            <Trash size={16} />
          </button>
        )}
      </div>
      {photos.length > 0 && (
        photos.length === 1 ? (
          <a href={`${API_URL}${photos[0]}?t=${entry.id}`} target="_blank" rel="noreferrer">
            <img
              src={`${API_URL}${photos[0]}?t=${entry.id}`}
              alt="avancement"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{ width: '100%', maxHeight: '360px', objectFit: 'cover', borderRadius: '8px', marginBottom: entry.message ? '8px' : 0, display: 'block' }}
            />
          </a>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(photos.length, 3)}, 1fr)`, gap: 4, marginBottom: entry.message ? '8px' : 0 }}>
            {photos.slice(0, 6).map((url, idx) => (
              <a key={idx} href={`${API_URL}${url}?t=${entry.id}-${idx}`} target="_blank" rel="noreferrer" style={{ position: 'relative' }}>
                <img
                  src={`${API_URL}${url}?t=${entry.id}-${idx}`}
                  alt={`avancement ${idx + 1}`}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '6px', display: 'block' }}
                />
                {idx === 5 && photos.length > 6 && (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 800, borderRadius: '6px' }}>+{photos.length - 6}</div>
                )}
              </a>
            ))}
          </div>
        )
      )}
      {entry.message && (
        <div style={{ fontSize: '0.92rem', color: '#1E2030', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{entry.message}</div>
      )}
    </div>
  );
};

const WorksiteFeed = ({ project, onBack, currentUser }) => {
  const [entries, setEntries] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [message, setMessage] = useState('');
  const [photos, setPhotos] = useState([]); // [{file, preview}]
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [subTab, setSubTab] = useState('feed');
  const fileRef = useRef(null);

  const fetchFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/worksite-feed/project/${project.id}`, { withCredentials: true });
      setEntries(res.data || []);
    } catch (err) {
      // 403 = not assigned to this project => display empty state instead of an error toast
      if (err?.response?.status !== 403) {
        toast.error('Erreur chargement du fil');
      }
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  const fetchGallery = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects/${project.id}/photos`, { withCredentials: true });
      setGalleryPhotos(res.data || []);
    } catch {
      /* noop - employees may not always have access */
    }
  }, [project.id]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);
  useEffect(() => { if (subTab === 'gallery') fetchGallery(); }, [subTab, fetchGallery]);

  const handleFiles = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of newFiles) {
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`Photo trop lourde (max 15 Mo) : ${f.name}`);
        continue;
      }
      accepted.push({ file: f, preview: URL.createObjectURL(f) });
    }
    if (accepted.length === 0) return;
    setPhotos(prev => [...prev, ...accepted]);
    if (fileRef.current) fileRef.current.value = '';
  };
  const removePhoto = (idx) => {
    setPhotos(prev => {
      const t = prev[idx];
      if (t?.preview) URL.revokeObjectURL(t.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };
  const clearAllPhotos = () => {
    photos.forEach(p => { if (p.preview) URL.revokeObjectURL(p.preview); });
    setPhotos([]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!message.trim() && photos.length === 0) return toast.error('Ajoutez un message ou une photo');
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append('message', message);
      photos.forEach(p => fd.append('files', p.file));
      await axios.post(`${API_URL}/api/worksite-feed/project/${project.id}`, fd, { withCredentials: true });
      setMessage('');
      clearAllPhotos();
      fetchFeed();
      toast.success(`Publie${photos.length > 1 ? ` (${photos.length} photos)` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    try {
      await axios.delete(`${API_URL}/api/worksite-feed/project/${project.id}/${id}`, { withCredentials: true });
      fetchFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div data-testid="worksite-feed">
      <button onClick={onBack} data-testid="feed-back-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#002FA7', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 0', marginBottom: '14px', fontSize: '0.9rem' }}>
        <ArrowLeft size={16} /> Retour aux chantiers
      </button>
      <div style={{ marginBottom: '16px', padding: '14px 18px', background: 'linear-gradient(135deg, #002FA7, #1E3A8A)', color: 'white', borderRadius: '10px' }}>
        <div style={{ fontSize: '0.75rem', opacity: 0.85, letterSpacing: '0.3px' }}>CHANTIER</div>
        <div style={{ fontSize: '1.25rem', fontWeight: '800' }}>{project.name}</div>
        {project.client_name && <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>{project.client_name}</div>}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E2E2E6', marginBottom: '16px', overflowX: 'auto' }}>
        {[
          { key: 'feed', label: 'Fil d\'avancement', icon: ImageSquare },
          { key: 'gallery', label: 'Galerie photos', icon: Camera },
          { key: 'documents', label: 'Documents', icon: FileText },
          { key: 'courses', label: 'Liste de courses', icon: ShoppingCart },
          { key: 'contacts', label: 'Contacts', icon: AddressBook },
          { key: 'reservations', label: 'SAV / Reserves', icon: Wrench },
        ].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} data-testid={`worksite-subtab-${t.key}`}
            style={{ padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: '600', backgroundColor: 'transparent', borderBottom: subTab === t.key ? '2px solid #002FA7' : '2px solid transparent', color: subTab === t.key ? '#002FA7' : '#5E5F66', marginBottom: '-2px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {subTab === 'reservations' ? (
        <ReservationsTab project={project} currentUser={currentUser} />
      ) : subTab === 'contacts' ? (
        <ProjectContactsTab project={project} currentUser={currentUser} />
      ) : subTab === 'documents' ? (
        <ProjectDocumentsViewer project={project} canManage={false} />
      ) : subTab === 'courses' ? (
        <MaterialsListTab project={project} isAdmin={false} />
      ) : subTab === 'gallery' ? (
        <PhotosTabGrouped
          photos={galleryPhotos}
          project={project}
          readOnly
          onUpload={() => setSubTab('feed')}
          onUpdateComment={() => {}}
          onDelete={() => {}}
          editingPhotoId={null}
          setEditingPhotoId={() => {}}
          editPhotoComment={''}
          setEditPhotoComment={() => {}}
          setPreviewPhoto={setPreviewPhoto}
        />
      ) : (
      <>
      <form onSubmit={handlePost} style={{ background: 'white', border: '1px solid #E2E2E6', borderRadius: '10px', padding: '14px 16px', marginBottom: '18px' }}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Decrivez l'avancement du jour..."
          data-testid="feed-message-input"
          rows={2}
          style={{ width: '100%', border: '1px solid #E2E2E6', borderRadius: '6px', padding: '8px 10px', resize: 'vertical', fontSize: '0.92rem', fontFamily: 'inherit' }}
        />
        {photos.length > 0 && (
          <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
            {photos.map((p, idx) => (
              <div key={idx} style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E2E2E6' }}>
                <img src={p.preview} alt={`preview ${idx + 1}`} style={{ width: '100%', height: '100px', objectFit: 'cover', display: 'block' }} />
                <button type="button" onClick={() => removePhoto(idx)} data-testid={`feed-photo-remove-${idx}`} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.65)', border: 'none', color: 'white', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" onChange={handleFiles} style={{ display: 'none' }} data-testid="feed-photo-input" />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="btn btn-secondary btn-sm"
            data-testid="feed-photo-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Camera size={16} /> Ajouter photo(s)
          </button>
          {photos.length > 0 && (
            <>
              <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{photos.length} photo(s)</span>
              <button type="button" onClick={clearAllPhotos} className="btn btn-secondary btn-sm" data-testid="feed-clear-photos">Tout retirer</button>
            </>
          )}
          <div style={{ flex: 1 }} />
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={posting}
            data-testid="feed-post-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <PaperPlaneRight size={16} weight="bold" /> {posting ? 'Publication...' : 'Publier'}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : entries.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66', background: 'white', border: '1px dashed #E2E2E6', borderRadius: '10px' }}>
          <ImageSquare size={40} style={{ opacity: 0.3, marginBottom: '10px' }} />
          <p style={{ margin: 0, fontSize: '0.9rem' }}>Aucune photo d'avancement pour le moment.<br/>Soyez le premier a publier !</p>
        </div>
      ) : (
        entries.map(e => <FeedEntry key={e.id} entry={e} currentUserId={currentUser?.id} isAdmin={isAdmin} onDelete={handleDelete} />)
      )}
      </>
      )}
      <PhotoPreviewModal photo={previewPhoto} onClose={() => setPreviewPhoto(null)} apiUrl={API_URL} />
    </div>
  );
};

const MyProjectsPage = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/me/projects`, { withCredentials: true });
      setProjects(res.data || []);
    } catch {
      toast.error('Erreur chargement chantiers');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="my-projects-page">
      {!selected ? (
        <>
          <div className="page-header">
            <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Buildings size={26} weight="bold" /> Mes chantiers
            </h1>
            <p className="page-subtitle">{projects.length} chantier(s) accessibles - publiez vos avancees en photos.</p>
          </div>

          {projects.length === 0 ? (
            <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>
              <User size={48} style={{ opacity: 0.3, marginBottom: '10px' }} />
              <p style={{ margin: 0 }}>Vous n'etes affecte a aucun chantier.<br/>Contactez votre administrateur.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
              {projects.map(p => <ProjectCard key={p.id} project={p} onClick={() => setSelected(p)} />)}
            </div>
          )}
        </>
      ) : (
        <WorksiteFeed project={selected} onBack={() => setSelected(null)} currentUser={user} />
      )}
    </div>
  );
};

export default MyProjectsPage;
